import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@stockline/ui/components/dropdown-menu";
import { Button } from "@stockline/ui/components/button";
import { Mail, MailOpen, MoreHorizontal, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ChatSessionActionsProps {
  sessionId: string;
  unread: boolean;
  notificationMode: boolean;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onClear: () => void;
}

export function ChatSessionActions({
  sessionId,
  unread,
  notificationMode,
  onMarkRead,
  onMarkUnread,
  onClear,
}: Readonly<ChatSessionActionsProps>) {
  const { t } = useTranslation();
  const actionsLabel = notificationMode
    ? t("common.aiManagement.sessionList.notificationActions")
    : t("common.actions");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1 h-6 w-6 text-muted-foreground opacity-70 transition-opacity hover:opacity-100 data-[state=open]:bg-accent data-[state=open]:opacity-100"
          onClick={(event) => event.stopPropagation()}
          aria-label={actionsLabel}
          title={actionsLabel}
          data-testid={`chat-session-actions-${sessionId}`}
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {notificationMode && (
          <>
            <DropdownMenuItem
              onClick={(event) => event.stopPropagation()}
              onSelect={() => {
                if (unread) onMarkRead();
                else onMarkUnread();
              }}
              data-testid={`chat-session-toggle-read-${sessionId}`}
            >
              {unread ? (
                <MailOpen className="h-4 w-4" />
              ) : (
                <Mail className="h-4 w-4" />
              )}
              {unread
                ? t("common.aiManagement.sessionList.markAsRead")
                : t("common.aiManagement.sessionList.markAsUnread")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem
          className="text-red-600 focus:text-red-600"
          onClick={(event) => event.stopPropagation()}
          onSelect={() => {
            onClear();
          }}
          data-testid={`chat-session-clear-${sessionId}`}
        >
          <Trash2 className="h-4 w-4" />
          {notificationMode
            ? t("common.aiManagement.sessionList.clearNotification")
            : t("common.delete")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
